import { _js, ___p } from '@ifixit/localize';
import { Utils } from 'Shared/utils';
import { StatusPanel } from 'Shared/status_panel';
import { Auth } from 'Shared/auth';
import { FormManager } from 'Shared/form_manager';
import { TiptapEditor } from 'Shared/prosemirror/tiptap/tiptap-lazy';
import {
   clearSessionStorage,
   getSerializedText,
   clearContent,
   resetContent,
   getEditor,
   saveState,
   getCharacterCount,
   clearError,
   hasUnsavedChanges,
} from 'Shared/prosemirror/tiptap/editor_cache';
import EditorCache from 'Shared/prosemirror/editorcache';
import { createRoot } from 'react-dom/client';
import { Initializer } from 'Shared/chakra-initialize';
import LoadingIndicator from 'Shared/loading_indicator';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';

const renderedEditors = {};

export function initCommentsFrameModule() {
   // Load this for the first comment-container on the block.
   const container = $E('.js-page-comments');
   if (container) {
      const commentContainer = container.closest('.js-comment-container');
      const manager = new CommentManager(container);
      commentContainer.store('commentManager', manager);
   }
}

function getEditorid(el) {
   return el.dataset.editorid;
}

export function scrollToEditor(el) {
   const editorid = getEditorid(el);
   const editorEl = document.getElementById(editorid);
   const editor = getEditor(editorid);
   const form = editorEl.getParent('form');

   form.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
   });
   // If this is called before the scroll animation is finished, it cuts it off.
   setTimeout(() => {
      editor?.commands.focus();
   }, 500);
}

export function CommentManager(container) {
   const self = this;
   const comments = getElement('.js-comments');
   const form = getElement('.js-add-comment-form');
   // Store the comment manager for the form as well, because it's easier to find
   // the form from the comment box
   form.store('commentManager', this);
   const formTitle = form.getElement('.js-add-comment-title');
   const replyCommentText = form.getElement('.js-reply-comment-text');
   const replyCancel = form.getElement('.js-cancel-reply');
   let chars = null;
   let maxCharacters = null;
   let minCharacters = null;
   const commentCount = getElement('.js-comment-count');
   let runValidator = false;
   const elementAbovePM = document.querySelector('.js-page-comments');

   if (elementAbovePM) {
      const showPMOptions = {
         root: null,
         rootMargin: '0px',
         threshold: 0,
      };

      const showProseMirror = entries => {
         const isInView = entries[0].target === elementAbovePM && entries[0].isIntersecting;

         if (isInView) {
            // select only the bottom comment form, do not select for each step
            const el = document.querySelector('.js-add-comment-form:not(.context-step)');
            setupEditor(el);
         }
      };

      const observer = new IntersectionObserver(showProseMirror, showPMOptions);
      observer.observe(elementAbovePM);
   }

   // These set when the form is opened and are sent along with the POST
   // that creates the comment.
   let context;
   let contextid;
   let parentid;

   if (form) {
      // eslint-disable-next-line no-var
      var formManager = new FormManager(form);

      addFormEvents(form);
   }

   addCommentActionHandlers(comments);

   function getSubmitHandler(el) {
      const parentDiv = el.parentNode;

      if (parentDiv.classList.contains('commentEditDiv')) {
         const submitBtnClass = parentDiv.get('data-submit-button');
         const submitBtn = parentDiv.getElementsByClassName(submitBtnClass)[0];
         return () => submitBtn.click();
      }

      const commentManager = getCommentManager(el);
      return commentManager.submit;
   }

   function getEditorProps(el) {
      return {
         preset: el.dataset.preset,
         text: el.dataset['text'],
         submitHandler: getSubmitHandler(el),
      };
   }

   function getCharacterCountConfig(el) {
      return {
         minCharacters: el.dataset.mincharacters,
         maxCharacters: el.dataset.maxcharacters,
      };
   }

   function refreshComments(loadedCallback) {
      const request = new Request.AjaxIO(getApiEndpoint())
         .send(context, contextid, false)
         .then(response => {
            comments.set('html', response.html);
            ModeratorVote.setupLinkActions();

            if (loadedCallback) {
               loadedCallback(comments);
            }
         });

      LoadingIndicator.withPromise(request, {
         hideSuccessMessage: true,
         pendingMessage: _js('Loading comments...'),
         failureMessage: _js('Error loading comments'),
      });
      return request;
   }

   this.loadComments = () => {
      if (comments.get('data-loaded')) {
         return Promise.resolve();
      }
      context = container.get('data-context');
      contextid = container.get('data-contextid');
      return refreshComments().then(() => {
         comments.set('data-loaded', 1);
      });
   };

   function toggleDeletedCommentsHandler(event) {
      event.stop();

      let text = _js('View Active');
      let newMode = 'deleted';
      let viewDeleted = true;

      if (this.get('data-mode') != 'active') {
         text = _js('View Deleted');
         newMode = 'active';
         viewDeleted = false;
      }

      new Request.AjaxIO(getApiEndpoint(), {
         onSuccess: response => {
            comments.set('html', response.html);
            ModeratorVote.setupLinkActions();
         },
      }).send(form.get('data-context'), form.get('data-contextid'), viewDeleted);

      this.set('html', text);
      this.set('data-mode', newMode);
   }

   function submit() {
      runValidator = true;
      const formManager = form.retrieve('FormManager:formManager');
      formManager.submit();
   }

   this.submit = submit;

   function cancelReply() {
      const editorid = getEditorid(form);
      parentid = undefined;
      replyCommentText.empty();
      if (formTitle) {
         formTitle.set('text', _js('Add Comment'));
      }
      context = form.get('data-context');
      contextid = form.get('data-contextid');
      replyCancel.addClass('hidden!');
      clearContent(editorid);
      clearSessionStorage(editorid);
      clearError(editorid);
   }

   this.cancelReply = cancelReply;

   function addFormEvents(form) {
      chars = form.getElement('.maxCharacters');
      maxCharacters = Number.parseInt(chars.get('text'), 10);
      minCharacters = Number.parseInt(chars.getProperty('data-minimum'), 10);

      // This is a workaround for Safari <10.1
      // If the link editor popup is in the DOM when the add comment form gets
      // a submit event, ignore the submit event.
      formManager.addSubmitHandler(() => {
         const linkEditor = form.querySelector('.ProseMirror-widget input.js-link-editor');

         return linkEditor !== document.activeElement;
      });

      formManager.addSubmitHandler(() => {
         if (!runValidator) {
            return;
         }

         const editorid = getEditorid(form);

         const charCount = getCharacterCount(editorid);
         let error = false;

         if (charCount <= 0) {
            error = _js('You cannot submit an empty comment!');
         } else if (charCount > maxCharacters) {
            /* Translators: %1 -> max characters; %2 -> character length; */
            error = _js(
               'Your comment is too long. Please keep it under %1 characters. It is currently %2 characters.',
               maxCharacters,
               charCount
            );
         } else if (charCount < minCharacters) {
            error =
               charCount == 1
                  ? /* Translators: %1 -> min characters; */
                    _js(
                       'Your comment is too short. Please write something over %1 characters. It is currently 1 character.',
                       minCharacters
                    )
                  : /* Translators: %1 -> min characters; %2 -> character length; */
                    _js(
                       'Your comment is too short. Please write something over %1 characters. It is currently %2 characters.',
                       minCharacters,
                       charCount
                    );
         }

         runValidator = false;
         if (error) {
            showError(error, editorid);
            return false;
         }
         return true;
      });

      formManager.addSubmitHandler(onSubmit);

      window.addEvents({
         'click:relay(.js-cancel-reply)': (ev, el) => {
            ev.stop();
            getCommentManager(el).cancelReply();
         },
         'click:relay(.js-add-comment)': (ev, el) => {
            setupCommentEditor(el);
         },
         'click:relay(.js-scroll-to-editor)': (ev, el) => {
            ev.event.stopImmediatePropagation();
            scrollToEditor(el);
         },
      });

      container.addEvents({
         'click:relay(.js-comment-submit)': () => {
            runValidator = true;
         },

         'click:relay(.js-show-page-comments)': event => {
            self.loadComments();
            // The button gets removed when the comments are loaded,
            // but lets disable it to prevent double clicking anyway.
            event.target.setAttribute('disabled', '1');
         },
      });

      if (container.id) {
         // Watch for click events on buttons (anywhere in the document) which
         // indicate they should use this comment form.
         $$('.addComment[data-containerid=' + container.id + '])').addEvent('click', setupForm);
      }

      getElements('.js-toggle-deleted').addEvent('click', toggleDeletedCommentsHandler);
   }

   function updateCommentCount(difference) {
      if (!commentCount) {
         return;
      }
      const prevCount = Number.parseInt(commentCount.get('data-numcomments'), 10);
      const count = prevCount + difference;

      const text =
         context === 'guide'
            ? ___p(count, '%1 Guide Comment', '%1 Guide Comments', count)
            : ___p(count, '%1 Comment', '%1 Comments', count);

      commentCount.set('data-numcomments', count);

      // Steps have a favicon and text within the commentCount element.
      // Let's make sure we only update the text and do not replace the favicon.
      if (context === 'step') {
         commentCount.getElement('.comment-text').set('text', text);
         // Ensure the css is correct without needing a refresh.
         if (count > 0) {
            commentCount.addClass('has-comments');
         } else {
            commentCount.removeClass('has-comments');
         }
         return;
      }

      commentCount.set('text', text);
   }

   async function copyPermalink(event) {
      event.stop();
      const permalink = this.dataset.permalink;
      await navigator.clipboard.writeText(permalink);
      StatusPanel.notify(_js('Link copied to clipboard.'), 3, { reload: false });
   }

   function addCommentActionHandlers(commentsContainer) {
      commentsContainer.addEvents({
         'click:relay(.js-permalink)': copyPermalink,
         'click:relay(.js-edit-comment)': editCommentHandler,
         'click:relay(.js-delete-comment)': deleteCommentHandler,
         'click:relay(.js-restore-comment)': restoreCommentHandler,
      });
   }

   function setupFormData(el) {
      const title = el.get('title') || _js('Add Comment');
      parentid = el.get('data-parentid');
      const replyToUsername = el.get('data-replyto-username');

      const replyToCommentid = el.get('data-replyto-commentid');
      const quotedCommentid = replyToCommentid || parentid;
      const comment = document.querySelector(
         `.commentContent[data-commentid="${CSS.escape(quotedCommentid)}"]`
      );
      const isReply = !!comment || !!replyToUsername;
      replyCommentText.empty();

      if (comment) {
         replyCommentText.append(comment.clone());
      }

      if (formTitle) {
         formTitle.set('text', title);
      }

      // Either clicked a seperate context (reply)
      // or we want the default one (normal comment)
      const overrideContext = el.get('data-context');
      const overrideContextid = el.get('data-contextid');

      context = isReply ? overrideContext : form.get('data-context');
      contextid = isReply ? overrideContextid : form.get('data-contextid');

      replyCancel.toggleClass('hidden!', !isReply);

      form.removeClass('hidden!');

      const editorid = getEditorid(form);
      const editorEl = document.getElementById(editorid);

      formManager.clear(editorEl);

      if (replyToUsername) {
         const editor = getEditor(editorid);
         if (editor) {
            editor
               .chain()
               .focus()
               .insertContent([
                  { type: 'mention', attrs: { id: replyToUsername } },
                  { type: 'text', text: ' ' },
               ])
               .run();
         }
      }
   }

   function editCommentHandler(event) {
      const editorid = getEditorid(this);

      setupEditor(this, false);

      event.stop();
      Auth.required({
         message: _js('Log in to edit a comment'),
         onAuthorize: () => {
            const comment = this.getParent('.comment');
            const content = comment.getFirst('.commentContent');
            const editDiv = comment.getFirst('.commentEditDiv');
            const commentid = this.get('data-commentid');

            const actions = getElements('.commentActions');
            actions.addClass('hidden!');

            content.hide();
            editDiv.show();
            const req = new Request.API_2_0('comments/' + commentid, {
               method: 'PATCH',
               onSuccess: response => {
                  trackCommentEdit();
                  editDiv.hide();
                  content.set('html', response.text_rendered);
                  content.show();
                  // Update starting state of the editor
                  clearSessionStorage(editorid);
                  saveState(editorid);
                  clearError(editorid);
                  actions.removeClass('hidden!');
               },

               onFailure: (xhr, message) => {
                  showApiError(xhr, message, editorid);
               },
            });

            editDiv.querySelector('.js-cancel-edit').addEvent('click', () => {
               content.show();
               editDiv.hide();

               clearError(editorid);
               resetContent(editorid);
               actions.removeClass('hidden!');
            });

            const submitHandler = editDiv.querySelector('.js-submit-edit');
            if (!submitHandler.hasClass('submit-handler')) {
               submitHandler.addClass('submit-handler').addEvent('click', eve => {
                  eve.stop();
                  const newText = getSerializedText(editorid).trim();
                  req.send({ text: newText });
               });
            }
         },
      });
   }

   function trackCommentEdit() {
      trackAnalyticsCustomEvent({
         eventCategory: 'Edit Comment',
         eventAction: 'Edit Comment',
      });
   }

   function destroyCommentElement(el) {
      const commentid = el.get('data-commentid');
      const comment = getElement('#comment-' + commentid);
      const replyParent = comment.getParent('.comment-reply');
      const commentParent = replyParent ? replyParent : comment.getParent('.comment-thread');

      let commentsDestroyed = 1;

      commentParent.destroy();
      if (replyParent === null) {
         const replies = comment.getSiblings('.comment-reply');
         commentsDestroyed += replies.length;
      }

      // Return the number of comment elements destroyed, to be used
      // in the total comment count.
      return commentsDestroyed;
   }

   function deleteCommentHandler(event) {
      event.stop();
      Auth.required({
         message: _js('Log in to delete a comment'),
         onAuthorize: () => {
            const commentid = this.get('data-commentid');
            const comment = getElement('#comment-' + commentid);
            if (comment) {
               const confirmResult = confirm(_js('Are you sure you want to delete this comment?'));

               if (confirmResult) {
                  new Request.API_2_0('comments/' + commentid, {
                     method: 'DELETE',
                     onSuccess: () => {
                        trackCommentDeletion();
                        const diff = destroyCommentElement(this);
                        updateCommentCount(-diff);
                     },
                  }).send();
               }
            }
         },
      });
   }

   function trackCommentDeletion() {
      trackAnalyticsCustomEvent({
         eventCategory: 'Delete Comment',
         eventAction: 'Delete Comment',
      });
   }

   function restoreCommentHandler(event) {
      event.stop();
      Auth.required({
         message: _js('Log in to restore a comment'),
         onAuthorize: () => {
            new Request.AjaxIO('restoreComment', {
               onSuccess: () => {
                  const diff = destroyCommentElement(this);
                  updateCommentCount(diff);
               },
            }).send(this.get('data-commentid'));
         },
      });
   }

   function showApiError(xhr, message, editorid) {
      showError(message, editorid);
   }

   function showError(message, editorid) {
      const editor = getEditor(editorid);

      StatusPanel.deactivate();
      editor.chain().showError(message).focus().run();
   }

   function onSubmit(el) {
      contextid = contextid || form.get('data-contextid');
      context = context || form.get('data-context');
      const editorid = getEditorid(el);

      Auth.required({
         message: _js('Log in to add a new comment'),
         onAuthorize: () => {
            StatusPanel.loading(_js('Adding note...'), 20);
            new Request.API_2_0('comments/' + context + '/' + contextid, {
               method: 'POST',
               onSuccess: () => {
                  trackGuideAndBlogComments(context);
                  StatusPanel.deactivate();
                  chars.hide();
                  chars.set('text', maxCharacters);
                  clearContent(editorid);
                  clearSessionStorage(editorid);
                  clearError(editorid);
                  setupFormData(form);
                  formTitle.set('text', _js('Add Comment'));
                  replyCancel.addClass('hidden!');
                  refreshComments(comments => {
                     updateCommentCount(/* difference = */ 1);

                     // Invalidate EditorCache entries for comments whose
                     // HTML is about to be replaced by refreshComments.
                     const childComments = comments.querySelectorAll('.comment');
                     Array.prototype.slice.call(childComments).forEach(comment => {
                        EditorCache.delete(comment.id);
                     });
                  });
               },
               onFailure: (xhr, message) => {
                  showApiError(xhr, message, editorid);
               },
               onComplete: () => {
                  StatusPanel.deactivate();
               },
            }).send({
               text: getSerializedText(editorid),
               parentid: parentid,
               langid: App.langid,
            });
         },
      });
   }

   // Track if the user commented on a guide, guide step, or blog post
   function trackGuideAndBlogComments(context) {
      const contextToTrackingName = new Map([
         ['guide', 'Guide'],
         ['step', 'Guide-Step'],
         ['wp_post', 'Blog Article'],
      ]);

      if (!contextToTrackingName.has(context)) {
         return;
      }

      const trackingName = contextToTrackingName.get(context);

      trackAnalyticsCustomEvent({
         eventCategory: `${trackingName} Comment`,
         eventAction: `${trackingName} Comment - Form “Add a Comment” - Submit`,
      });
   }

   function getElement(selector) {
      return container.getElement(selector);
   }

   function getElements(selector) {
      return container.getElements(selector);
   }

   function setupEditor(el, useCharacterCount = true) {
      const editorId = el.dataset['editorid'];
      const editorEl = document.getElementById(editorId);

      if (renderedEditors[editorId]) {
         const editor = getEditor(editorId);

         if (editor && el.dataset['text']) {
            editor.commands.setContent(el.dataset['text'], { emitUpdate: false });
         }
      } else {
         renderedEditors[editorId] = true;

         const props = getEditorProps(editorEl);
         const characterCountConfig = useCharacterCount ? getCharacterCountConfig(editorEl) : null;
         const EditorComponent = Initializer(TiptapEditor);
         const root = createRoot(editorEl);
         root.render(
            <EditorComponent
               editorid={editorId}
               characterCountConfig={characterCountConfig}
               {...props}
            />
         );

         Utils.UnsavedChanges.addHandler(() => {
            if (hasUnsavedChanges(editorId)) {
               return _js(
                  'Your comments have not yet been posted. Your changes will be lost if you continue.'
               );
            }
         });
      }
   }

   /**
    * Event handler to be used when registering click events on
    * buttons that should show the comment form.
    */
   function setupForm(el) {
      setupEditor(el);
      setupFormData(el);
   }

   function getCommentManager(el) {
      const commentContainer = el.closest('.js-comment-container');
      const manager =
         commentContainer.retrieve('commentManager') || createCommentManager(commentContainer);
      commentContainer.store('commentManager', manager);
      return manager;
   }

   function createCommentManager(commentContainer) {
      const manager = new CommentManager(commentContainer);
      commentContainer.store('commentManager', manager);
      return manager;
   }

   function setupCommentEditor(el) {
      const manager = getCommentManager(el);
      manager.setupCommentEditor(el);
   }

   this.setupCommentEditor = el => {
      setupForm(el);
   };

   function getApiEndpoint() {
      return container.get('data-api') || 'getGroupedCommentsHtml';
   }
}
